<template>
  <v-app-bar
    class="reward-link-header"
    app
    fixed
    color="white"
    :height="appBarHeight"
    :extension-height="extensionHeight"
  >
    <div class="header-contents">
      <div class="logo">
        <router-link
          :aria-label="$t('home')"
          :to="{ name: logoRouteName }"
        >
          <v-img
            :alt="$t('rewardlink-brand')"
            src="/doom-toot-poster.jpg"
            contain
            height="100%"
            width="100%"
            class="company-logo"
          />
        </router-link>
      </div>
      <div class="content">
        <div class="history-button-mobile">
          <redemption-history-btn
            v-if="displayRedemptionHistoryBtn"
            id="history-btn-mobile"
            mobile
          />
        </div>
        <div class="description-list">
          <div
            v-if="rewardLink"
            id="balance-container"
            class="remaining-balance"
          >
            <description-list-item
              id="sv-balance"
              class="title"
              :label="$t('header-balance')"
              :description="balanceToDisplay.amount"
              :hide-label="$vuetify.breakpoint.mobile"
              :icon="balanceToDisplay.icon"
              :icon-color="balanceToDisplay.iconColor"
            />
            <expire-date
              :expiration-date="rewardLink.expirationDate"
              :expired="isExpired"
              tabindex="0"
            />
          </div>
        </div>
        <v-toolbar-items class="toolbar-items justify-end align-center">
          <redemption-history-btn
            v-if="displayRedemptionHistoryBtn"
            id="history-btn"
          />
          <cart-menu
            v-if="showCart"
            attach-menu-to="#svp-head"
          />
          <div
            v-else
            class="cart-spacer"
          />
        </v-toolbar-items>
      </div>
    </div>
    <template
      v-if="showCatalogSearch"
      #extension
    >
      <v-container
        fluid
        class="search-container"
      >
        <catalog-search @show-filters="showFilters" />
      </v-container>
    </template>
  </v-app-bar>
</template>

<script>
import { mapGetters } from 'vuex'
import CartMenu from '@/components/cart/CartMenu'
import ExpireDate from '@/components/shared/ExpireDate'
import DescriptionListItem from '@/components/shared/DescriptionListItem'
import CatalogSearch from '@/components/shared/catalog/CatalogSearch'
import RedemptionHistoryBtn from '@/components/history/RedemptionHistoryBtn'
import {
  REVIEW_AND_COMPLETE_ROUTE,
  CATALOG_ROUTE,
  REDEMPTION_HISTORY_ROUTE,
  DEFAULT_ROUTE, DIRECT_PAYMENTS_ROUTE
} from '@/router/routes/route-names'
import { getNonExistingRouteNameEquivalent } from '@/router/routes/utilities'
import {
  HEADER_HEIGHT,
  HEADER_HEIGHT_SM,
  HEADER_EXTENSION_HEIGHT,
  HEADER_EXTENSION_HEIGHT_WITH_FILTERS
} from '@/components/header.constants'
import { TANGO_CARD_LOGO_URL } from '@/components/shared/company-logo'
import * as getTypes from '@/store/get-types'

export default {
  name: 'Header',
  components: {
    CartMenu,
    ExpireDate,
    DescriptionListItem,
    CatalogSearch,
    RedemptionHistoryBtn
  },
  props: {
    isExpired: {
      type: Boolean,
      required: false,
      default: false
    },
    isRedeemed: {
      type: Boolean,
      required: false,
      default: false
    },
    isLocked: {
      type: Boolean,
      required: false,
      default: false
    },
    existingRewardLinkExists: {
      type: Boolean,
      required: false,
      default: false
    },
    logoSource: {
      type: String,
      required: false,
      default: TANGO_CARD_LOGO_URL
    },
    height: {
      type: [String, Number],
      default: HEADER_HEIGHT
    },
    heightSm: {
      type: [String, Number],
      default: HEADER_HEIGHT_SM
    }
  },
  data () {
    return {
      filtersShown: true
    }
  },
  computed: {
    ...mapGetters({
      rewardLink: getTypes.GET_REWARD_LINK,
      catalogSearchIsVisible: getTypes.GET_SEARCH_IS_VISIBLE,
      directPaymentAmount: getTypes.GET_DIRECT_PAYMENT_AMOUNT
    }),
    showCart () {
      return (
        this.rewardLink &&
        !this.onCheckoutOrCompleteRoute &&
        !this.isExpired &&
        !this.isRedeemed &&
        !this.isLocked
      )
    },
    onCheckoutOrCompleteRoute () {
      const currentRouteName = this.$route.name
      const checkoutOrCompleteRoutes = [
        'Checkout',
        'Complete',
        'Cart',
        REVIEW_AND_COMPLETE_ROUTE
      ]
      return checkoutOrCompleteRoutes.includes(currentRouteName)
    },
    logoRouteName () {
      return this.existingRewardLinkExists
        ? CATALOG_ROUTE
        : getNonExistingRouteNameEquivalent({ name: DEFAULT_ROUTE })
    },
    companyLogo () {
      return {
        url: this.logoSource || TANGO_CARD_LOGO_URL
      }
    },
    appBarHeight () {
      return this.$vuetify.breakpoint.smAndDown ? this.heightSm : this.height
    },
    showCatalogSearch () {
      return this.$route.name === CATALOG_ROUTE && !this.catalogSearchIsVisible
    },
    extensionHeight () {
      return this.filtersShown
        ? HEADER_EXTENSION_HEIGHT_WITH_FILTERS
        : HEADER_EXTENSION_HEIGHT
    },
    displayRedemptionHistoryBtn () {
      return (
        this.$route.name !== REDEMPTION_HISTORY_ROUTE
      )
    },
    isMakingDirectPayment () {
      return this.$route.matched.some(({ name }) => name === DIRECT_PAYMENTS_ROUTE)
    },
    balanceToDisplay () {
      const balanceDetails = {
        amount: null,
        icon: null,
        iconColor: null
      }
      if (this.isLocked) {
        balanceDetails.amount = this.$money(this.rewardLink?.currentAmount, this.rewardLink?.currencyCode)
        balanceDetails.icon = 'mdi-lock'
        balanceDetails.iconColor = 'error'
      } else {
        let amount = this.rewardLink?.availableAmount
        if (this.isMakingDirectPayment) {
          amount -= this.directPaymentAmount
        }
        balanceDetails.amount = this.$money(amount, this.rewardLink?.currencyCode)
      }
      return balanceDetails
    }
  },
  methods: {
    showFilters (show) {
      this.filtersShown = show
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/assets/css/variables';
$mobile-breakpoint: 'only screen and (max-width: #{$breakpoint-md - 1})';
$desktop-breakpoint: 'only screen and (min-width: #{$breakpoint-md})';
$xs-breakpoint: 'only screen and (min-width: 360px)';
$sm-breakpoint: 'only screen and (min-width: 381px)';
$lg-breakpoint: 'only screen and (min-width: 960px)';

.reward-link-header {
  padding: 0 0 0 0;
  margin-right: 0;
  margin-left: 0;
  z-index: 10 !important;
}

.header-contents {
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;

  .logo {
    display: flex;
    align-items: center;
    &,
    > a {
      width: 250px;
      height: 70px;
    }
    @media #{$mobile-breakpoint} {
      justify-content: center;
      padding: 0;
      height: 48px;
      overflow: hidden;
      width: 100%;
      a {
        width: 200px;
        height: 40px;
      }
    }
  }
  .content {
    display: flex;
    align-items: center;
    flex-grow: 1;
    min-height: 45px;
    @media #{$desktop-breakpoint} {
      height: 100%;
    }

    #history-btn {
      display: none;
      @media #{$desktop-breakpoint} {
        display: block;
      }
    }

    .history-button-mobile {
      min-width: 72px;
      @media #{$desktop-breakpoint} {
        display: none;
      }
    }

    .description-list {
      flex-grow: 1;
    }

    .toolbar-items {
      min-width: 72px;
      padding-right: 4px;
      @media #{$desktop-breakpoint} {
        min-width: 250px;
      }
      @media #{$lg-breakpoint} {
        min-width: 280px;
      }
      .cart-spacer {
        width: 68px;
        @media #{$lg-breakpoint} {
          width: 72px;
        }
      }
    }
  }
}

::v-deep {
  .v-toolbar__content {
    padding: 0;
  }
  .v-toolbar__extension {
    padding: 0 !important;

    .search-container {
      max-width: 1276px;
      padding-top: 0;
      padding-bottom: 0;
    }
  }

  .company-logo {
    @media #{$desktop-breakpoint} {
      margin-left: 15px;
    }
  }
  #history-btn-mobile {
    @media only screen and (max-width: 320px) {
      padding: 0;
      min-width: 52px;
    }
  }
}
</style>
